import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../../lib/authedFetch';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash2, Target, Pencil, Star } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, ReferenceLine, Tooltip as RechartsTooltip } from 'recharts';
import { toast } from 'sonner';
import secureStorage from '../../lib/secureStorage';
import logger from '../../lib/logger';
import { PageShell } from '../../components/layout/PageShell';
import { PageHero } from '../../components/layout/PageHero';
import { MetricHeroStats } from '../../components/layout/MetricHeroStats';
import { getLightSectionTheme } from '../../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hoisted static chart margin to avoid inline-object re-render churn.
const CHART_MARGIN = { top: 5, right: 20, bottom: 5, left: 0 };

export default function PEGoalsPage() {
  const { isSuperAdmin, viewAsOrg } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(viewAsOrg?.id || '');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [goals, setGoals] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [form, setForm] = useState({ target_pe: 0.80, target_capability: 0.80, target_motivation: 0.80, target_retention: 0.85, target_lei: 0.75, target_date: '2026-12' });

  const headers = useCallback(() => {
    const h = { 'Authorization': `Bearer ${secureStorage.getItem('session_token')}`, 'Content-Type': 'application/json' };
    if (viewAsOrg?.id) h['X-View-As-Org'] = viewAsOrg.id;
    return h;
  }, [viewAsOrg]);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const res = await authedFetch(`${API_URL}/api/people-engine/organizations`, { headers: headers() });
        if (res.ok) {
          const data = await res.json();
          setOrganizations(data.organizations || []);
          setSelectedOrg(prev => prev || (data.organizations?.length > 0 ? data.organizations[0].id : ''));
        }
      } catch (err) { logger.error('Failed to fetch organizations:', err); }
    };
    fetchOrgs();
  }, [headers]);

  useEffect(() => {
    if (!selectedOrg) return;
    const fetchCohorts = async () => {
      try {
        const res = await authedFetch(`${API_URL}/api/people-engine/cohorts?organization_id=${selectedOrg}`, { headers: headers() });
        if (res.ok) setCohorts((await res.json()).cohorts || []);
      } catch (err) { logger.error('Failed to fetch cohorts:', err); }
    };
    fetchCohorts();
  }, [selectedOrg, headers]);

  const fetchData = useCallback(async () => {
    if (!selectedOrg) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ organization_id: selectedOrg });
      if (selectedCohort !== 'all') params.append('cohort_id', selectedCohort);

      const [healthRes, goalsRes] = await Promise.all([
        authedFetch(`${API_URL}/api/people-engine/health?${params}`, { headers: headers() }),
        authedFetch(`${API_URL}/api/people-engine/goals?${params}`, { headers: headers() }),
      ]);

      if (healthRes.ok) setHealthData(await healthRes.json());
      if (goalsRes.ok) setGoals((await goalsRes.json()).goals || []);
    } catch (e) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  }, [selectedOrg, selectedCohort, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setEditingGoalId(null);
    setForm({ target_pe: 0.80, target_capability: 0.80, target_motivation: 0.80, target_retention: 0.85, target_lei: 0.75, target_date: '2026-12' });
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (goal) => {
    setEditingGoalId(goal.id);
    setForm({
      target_pe: goal.target_pe ?? 0.80,
      target_capability: goal.target_capability ?? 0.80,
      target_motivation: goal.target_motivation ?? 0.80,
      target_retention: goal.target_retention ?? 0.85,
      target_lei: goal.target_lei ?? 0.75,
      target_date: goal.target_date || '2026-12',
    });
    setShowDialog(true);
  };

  const handleSaveGoal = async () => {
    const isEdit = !!editingGoalId;
    try {
      const payload = {
        target_pe: parseFloat(form.target_pe),
        target_capability: parseFloat(form.target_capability),
        target_motivation: parseFloat(form.target_motivation),
        target_retention: parseFloat(form.target_retention),
        target_lei: parseFloat(form.target_lei),
        target_date: form.target_date,
      };
      if (!isEdit) {
        payload.organization_id = selectedOrg;
        payload.cohort_id = selectedCohort !== 'all' ? selectedCohort : null;
      }
      const url = isEdit
        ? `${API_URL}/api/people-engine/goals/${editingGoalId}`
        : `${API_URL}/api/people-engine/goals`;
      const res = await authedFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: headers(),
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(isEdit ? 'Goal updated' : 'Goal created!');
        setShowDialog(false);
        resetForm();
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to ${isEdit ? 'update' : 'create'} goal`);
      }
    } catch {
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} goal`);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/goals/${goalId}`, {
        method: 'DELETE', headers: headers(),
      });
      if (res.ok) {
        toast.success('Goal deleted');
        fetchData();
      }
    } catch { toast.error('Failed to delete goal'); }
  };

  const handleActivateGoal = async (goalId) => {
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/goals/${goalId}/activate`, {
        method: 'POST', headers: headers(),
      });
      if (res.ok) {
        toast.success('Goal set as active');
        fetchData();
      } else {
        toast.error('Failed to activate goal');
      }
    } catch { toast.error('Failed to activate goal'); }
  };

  // Build forecast vs actuals chart data
  const trends = healthData?.trends || [];
  const activeGoal = goals[0]; // Use most recent goal

  const chartData = trends.map(t => ({
    period: t.period,
    actual_pe: t.pe,
    actual_cap: t.capability,
    actual_mot: t.motivation,
    actual_ret: t.retention,
    actual_lei: t.lei,
  }));

  const __pageTheme = getLightSectionTheme('PE');

  // Hero stats — mirror PE Diagnostic's 5-tile layout. Each component
  // tile overlays actual vs active-goal target and a gap delta so
  // users see the headline picture without having to scroll.
  const fmtPct = (v) => (v == null || Number.isNaN(v) ? '—' : `${(v * 100).toFixed(1)}%`);
  const fmtTarget = (v) => (v == null ? null : `${Math.round(v * 100)}%`);
  const fmtGap = (actual, target) => {
    if (actual == null || target == null) return null;
    const d = actual - target;
    const pp = (d * 100).toFixed(1).replace(/\.0$/, '');
    return d >= 0 ? { text: `+${pp}pp above`, tone: 'above' } : { text: `${pp}pp below`, tone: 'below' };
  };

  const peCurrent = healthData?.current || null;

  const heroTiles = peCurrent
    ? [
        { key: 'pe', label: 'PE Score', actual: peCurrent.pe, target: activeGoal?.target_pe, testId: 'pe-goals-stat-pe', headline: true },
        { key: 'capability', label: 'Capability', actual: peCurrent.capability, target: activeGoal?.target_capability, testId: 'pe-goals-stat-capability' },
        { key: 'motivation', label: 'Motivation', actual: peCurrent.motivation, target: activeGoal?.target_motivation, testId: 'pe-goals-stat-motivation' },
        { key: 'retention', label: 'Retention', actual: peCurrent.retention, target: activeGoal?.target_retention, testId: 'pe-goals-stat-retention' },
        { key: 'lei', label: 'Learning Effectiveness', actual: peCurrent.lei ?? peCurrent.le, target: activeGoal?.target_lei, testId: 'pe-goals-stat-lei' },
      ]
    : [];

  // Each tile wraps actual/target into a compound value node and
  // routes its gap-delta through hintTone=above|below so the shared
  // MetricHeroStats primitive can render the colored subtitle.
  const mappedTiles = heroTiles.map((t) => {
    const gap = fmtGap(t.actual, t.target);
    return {
      key: t.key,
      label: t.label,
      headline: t.headline,
      testId: t.testId,
      value: (
        <span className="inline-flex items-baseline gap-1.5">
          <span>{fmtPct(t.actual)}</span>
          {t.target != null && (
            <span className="text-xs font-semibold text-slate-500 tabular-nums">
              / {fmtTarget(t.target)}
            </span>
          )}
        </span>
      ),
      hint: gap ? (
        <span data-testid={`${t.testId}-gap`}>{gap.text}</span>
      ) : (
        activeGoal ? 'No target set' : 'Set a goal to track progress'
      ),
      hintTone: gap ? gap.tone : 'default',
    };
  });

  const heroStats = peCurrent ? (
    <MetricHeroStats tiles={mappedTiles} testId="pe-goals-hero-stats" />
  ) : null;

  const heroActions = (
    <Button
      size="sm"
      onClick={openCreateDialog}
      className="gap-1.5 bg-white border border-slate-200 border text-slate-700 hover:bg-slate-50"
      data-testid="create-pe-goal-btn"
    >
      <Plus className="h-4 w-4" /> Set Goal
    </Button>
  );

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="People Engine Goals"
        description="Set People Engine targets and track actuals vs targets for the PE composite, Capability, Motivation, Retention, and Learning Effectiveness components. Pick a cohort to localise the picture and see where you're trending above or below plan."
        theme={__pageTheme}
        icon={Target}
        actions={heroActions}
        stats={heroStats}
        testId="pe-goals-hero"
      />
    )}>
    <div className="space-y-6" data-testid="pe-goals-page">
      {/* Filters */}
      <div className="flex items-center gap-3">
        {isSuperAdmin && organizations.length > 0 && (
          <Select value={selectedOrg} onValueChange={(v) => { setSelectedOrg(v); setSelectedCohort('all'); }}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Select organization..." /></SelectTrigger>
            <SelectContent>
              {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={selectedCohort} onValueChange={setSelectedCohort}>
          <SelectTrigger className="w-56"><SelectValue placeholder="All Cohorts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cohorts</SelectItem>
            {cohorts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Forecast vs Actuals Chart */}
          <Card data-testid="pe-forecast-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">PE Score: Actuals vs Target</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 11 }} />
                  <RechartsTooltip formatter={(v) => `${(v * 100).toFixed(1)}%`} />
                  <Legend />
                  <Line type="monotone" dataKey="actual_pe" name="Actual PE" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 3 }} />
                  {activeGoal && (
                    <ReferenceLine y={activeGoal.target_pe} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `Target: ${(activeGoal.target_pe * 100).toFixed(0)}%`, position: 'right', fill: '#ef4444', fontSize: 11 }} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Goals List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-4 w-4" /> Active Goals
              </CardTitle>
            </CardHeader>
            <CardContent>
              {goals.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">No goals set yet. Click "Set Goal" to create one.</p>
              ) : (
                <div className="space-y-3">
                  {goals.map((g, idx) => (
                    <div
                      key={g.id}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        idx === 0
                          ? 'border-emerald-200 bg-emerald-50/50'
                          : 'border-slate-100'
                      }`}
                      data-testid={`goal-row-${g.id}`}
                      data-goal-active={idx === 0 ? 'true' : 'false'}
                    >
                      <div className="flex items-center gap-4 flex-wrap">
                        {idx === 0 && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700"
                            title="Targets from this goal are driving the hero stats"
                            data-testid="goal-active-badge"
                          >
                            Active
                          </span>
                        )}
                        <div className="text-sm">
                          <span className="font-medium text-slate-700">PE Target: {(g.target_pe * 100).toFixed(0)}%</span>
                          <span className="text-slate-400 mx-2">|</span>
                          <span className="text-xs text-slate-500">
                            C: {g.target_capability ? `${(g.target_capability * 100).toFixed(0)}%` : '—'} | M: {g.target_motivation ? `${(g.target_motivation * 100).toFixed(0)}%` : '—'} | R: {g.target_retention ? `${(g.target_retention * 100).toFixed(0)}%` : '—'} | LE: {g.target_lei ? `${(g.target_lei * 100).toFixed(0)}%` : '—'}
                          </span>
                        </div>
                        <span className="text-xs text-slate-400">Target: {g.target_date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {idx !== 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleActivateGoal(g.id)}
                            data-testid={`activate-goal-${g.id}`}
                            title="Set as active — use these targets for hero + forecast chart"
                          >
                            <Star className="h-4 w-4 text-amber-500" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(g)} data-testid={`edit-goal-${g.id}`} title="Edit goal">
                          <Pencil className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteGoal(g.id)} data-testid={`delete-goal-${g.id}`} title="Delete goal">
                          <Trash2 className="h-4 w-4 text-rose-400" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Create/Edit Goal Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGoalId ? 'Edit People Engine Goal' : 'Set People Engine Goal'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Target PE Score (0–1)</Label>
                <Input type="number" min={0} max={1} step={0.01} value={form.target_pe} onChange={e => setForm({ ...form, target_pe: e.target.value })} data-testid="goal-pe-input" />
              </div>
              <div>
                <Label>Target Date</Label>
                <Input type="month" value={form.target_date} onChange={e => setForm({ ...form, target_date: e.target.value })} data-testid="goal-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Capability Target (0–1)</Label>
                <Input type="number" min={0} max={1} step={0.01} value={form.target_capability} onChange={e => setForm({ ...form, target_capability: e.target.value })} />
              </div>
              <div>
                <Label>Motivation Target (0–1)</Label>
                <Input type="number" min={0} max={1} step={0.01} value={form.target_motivation} onChange={e => setForm({ ...form, target_motivation: e.target.value })} />
              </div>
              <div>
                <Label>Retention Target (0–1)</Label>
                <Input type="number" min={0} max={1} step={0.01} value={form.target_retention} onChange={e => setForm({ ...form, target_retention: e.target.value })} />
              </div>
              <div>
                <Label>LEI Target (0–1)</Label>
                <Input type="number" min={0} max={1} step={0.01} value={form.target_lei} onChange={e => setForm({ ...form, target_lei: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSaveGoal} data-testid="save-goal-btn">
              {editingGoalId ? 'Save Changes' : 'Create Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
}
